import React, { PropsWithChildren, ReactNode } from 'react'
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form'
import { classNames, designSystem } from '@/util/tailwindHelpers'

import ListModalTrigger from '@/components/BulkAssetsActionView/ListModalTrigger'
import { BulkAssetListConfig } from './types'
import ListColumn from './ListColumn'
import { ModalCloseButton } from '@/components/Modals/ModalV2/ModalCloseButton'
import Button from '@/components/Button'
import {
  ModalButtonProps,
  ModalChildrenWrappers
} from '../Modals/ModalV2/types'

interface Props<T> extends PropsWithChildren {
  className?: string
  modalProps: Omit<ModalChildrenWrappers, 'ModalHeaderMedia'>
  primaryAction: Partial<ModalButtonProps>
  onClose: () => void
  onSubmit: (data: T) => void
  formMethods: UseFormReturn<T, any, undefined>
  title: string
  actionLabel?: string
  listConfig: BulkAssetListConfig
  footerSlot?: ReactNode
  description?: string
  subFooter?: ReactNode
}

export default function BulkAssetsActionView<T extends FieldValues>({
  className,
  onClose,
  primaryAction,
  title,
  actionLabel,
  formMethods,
  description,
  children,
  onSubmit,
  modalProps,
  footerSlot,
  subFooter,
  listConfig
}: Props<T>) {
  const {
    handleSubmit,
    formState: { isValid, isSubmitting }
  } = formMethods

  const { ModalBody, ModalFooter } = modalProps
  const { onListModalTriggerClick } = listConfig

  const isLoading = primaryAction.loading || isSubmitting
  const isDisabled = primaryAction.disabled || !isValid
  const submitButtonProps = {
    ...primaryAction,
    loading: isLoading,
    disabled: isDisabled
  }

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={classNames('flex-1', className)}
      >
        <ModalBody className="max-md:!min-h-[50vh]">
          <div
            style={{ '--containerPadding': designSystem.spacing[3] }}
            className={classNames(
              'grid md:grid-cols-2 -mx-[--modalPadding] -mb-[--modalPadding] rounded-[--borderRadius] overflow-hidden ',
              className
            )}
          >
            <div className="flex-1 flex-col gap-3 flex md:py-[--modalPadding] px-[--modalPadding] min-h-0 bg-gray-850">
              <div className="flex-col flex flex-1 min-h-0 gap-2">
                <ModalCloseButton
                  className="max-md:hidden flex-shrink-0 !ml-0"
                  onClick={onClose}
                />

                <h1 className="h4 max-md:hidden">{title}</h1>
                {description && (
                  <p className="body-sm text-gray-200">{description}</p>
                )}

                <div className="flex-1 flex flex-col justify-between gap-3 min-h-0">
                  <div className="flex-1 min-h-0">
                    {children}

                    <ListModalTrigger
                      actionLabel={actionLabel}
                      count={listConfig.items?.length}
                      isLoading={listConfig.isLoading}
                      className="md:hidden"
                      onClick={onListModalTriggerClick}
                    />

                    {footerSlot}
                  </div>
                </div>
                <div>
                  <SubmitButton
                    className="max-md:hidden"
                    {...submitButtonProps}
                  />
                  {subFooter}
                </div>
              </div>
            </div>

            <ListColumn
              className="max-md:hidden bg-gray-800 px-[--modalPadding] pt-[--modalPadding]"
              listConfig={listConfig}
            />
          </div>
        </ModalBody>
        <ModalFooter className="md:hidden">
          <SubmitButton {...submitButtonProps} />
        </ModalFooter>
      </form>
    </FormProvider>
  )
}

const SubmitButton = ({
  className,
  label = 'Submit',
  ...props
}: ModalButtonProps & { className?: string }) => {
  return (
    <Button
      type="submit"
      {...props}
      className={classNames('flex-shrink-0 w-full', className)}
    >
      {label}
    </Button>
  )
}
